import { create } from 'zustand';

/**
 * Acting-tenant / acting-org / session store (contracts.md §2). Per-tab state
 * deliberately — sessionStorage-backed, never localStorage: switching tenant or
 * org in one tab must never redirect in-flight writes in another tab.
 *
 * EAA-1797 (P4b): `actingOrgId` is the console org picker's selection — the org
 * the operator is acting AS. It rides on every console request as `X-Org-Id`
 * (see the `request()` helpers in `api/orgAdmin.ts` / `api/console.ts`) and is
 * validated against `GET /me/acting-org` (the P4a seam, EAA-1796). Persisted to
 * sessionStorage so a reload keeps the choice within the same tab.
 */
const ACTING_ORG_KEY = 'portal.actingOrgId';

function readActingOrg(): string | null {
    try {
        return window.sessionStorage.getItem(ACTING_ORG_KEY);
    } catch {
        // Private-mode / storage-disabled: fall back to in-memory only.
        return null;
    }
}

function persistActingOrg(orgId: string | null): void {
    try {
        if (orgId) {
            window.sessionStorage.setItem(ACTING_ORG_KEY, orgId);
        } else {
            window.sessionStorage.removeItem(ACTING_ORG_KEY);
        }
    } catch {
        // Ignore — the in-memory store value is still authoritative for this tab.
    }
}

interface SessionState {
    activeTenantId: string | null;
    setActiveTenantId: (tenantId: string | null) => void;
    actingOrgId: string | null;
    setActingOrgId: (orgId: string | null) => void;
}

export const useSessionStore = create<SessionState>((set) => ({
    activeTenantId: null,
    setActiveTenantId: (tenantId) => set({ activeTenantId: tenantId }),
    actingOrgId: readActingOrg(),
    setActingOrgId: (orgId) => {
        persistActingOrg(orgId);
        set({ actingOrgId: orgId });
    },
}));
